<template>
  <div>
    <transition name="fade">
      <GuideVideoBanner
        v-if="showGuideCard && text"
        :text="text"
        :subtext="subText"
        :videoUrl="videoUrl"
        :videoThumbnail="videoThumbnail"
        @close="handleGuideClose"
      />
    </transition>
    <div class="your-badges-tab">
      <YourBadgesTable
        :data-stream="employeeBadges"
        :level-counts="levelCounts"
        :initial-expiration-filter="listQuery.expirationFilter"
        :initial-badge-standing-filters="listQuery.badgeStandingFilters"
        :loading="loading"
        server-side-pagination
        :pagination="pagination"
        @pagination-change="onBadgePageChange"
        @badges-query-change="onBadgesQueryChange"
        @chatter-click="handleChatterClick"
        @expiration-synced="fetchEmployeeBadges"
      />
    </div>
    <ChatterDrawer
      :drawer="showChatterDrawer"
      type="Badge"
      :typeValue="selectedBadgeChatter?.badgeLevelName || ''"
      :listData="selectedBadgeChatter"
      object-type="badge"
      :object-id="selectedBadgeChatter?.id ?? null"
      @closeDrawer="closeChatterDrawer"
      iconName="org_icon_responsibility"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalChatter"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch } from "vue";
import { useRoute } from "vue-router/composables";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import YourBadgesTable from "@components/YourBadgesTable/YourBadgesTable.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { getEmployeeBadges, getAllEmployeeBadges, getChatterList } from "@/api";
import { formatBadgeChatterNote } from "@/utils/chatterUtils";
import {
  BADGE_EXPIRATION_FILTER_NONE,
  BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE,
  BADGE_STANDING_FILTER_OPTIONS,
  filterBadgesByStanding,
  filterCurrentBadgesAboutToExpire,
  isAboutToExpireExpirationFilter,
  isAboutToExpireRouteQuery,
  isObsoleteStandingRouteQuery,
} from "@/utils/badgeExpiration";
import {
  buildEmployeeBadgesListQuery,
  mapEmployeeBadgeApiResultsToTableRows,
  needsClientSideBadgeListFiltering,
  normalizeBadgeLevelCounts,
} from "@/utils/employeeBadgeListUtils";
import { resolveStoredAuthEmployeeId } from "@components/utils/authEmployeeContext";

const { proxy } = getCurrentInstance();
const route = useRoute();

const props = defineProps({
  dataStream: {
    type: Array,
    default: () => [],
  },
  text: {
    type: String,
    default: "",
  },
  subText: {
    type: String,
    default: "",
  },
  videoUrl: {
    type: String,
    default: "",
  },
  videoThumbnail: {
    type: String,
    default: "",
  },
  showGuideCard: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['guide-close']);

const employeeBadges = ref([]);
/** Optional totals per proficiency level (API `level_counts`) for the active filters. */
const levelCounts = ref(null);
const listQuery = ref({
  search: "",
  appliedFilters: ["Current"],
  selectedLevels: [],
  expirationFilter: BADGE_EXPIRATION_FILTER_NONE,
  badgeStandingFilters: [],
});
/** Page size sent as `limit` to the badges API (fixed at 10). */
const pagination = ref({
  page: 1,
  total: 0,
  perPage: 10,
});
const showChatterDrawer = ref(false);
const selectedBadgeChatter = ref(null);
const chatterData = ref([]);
const chatterPage = ref(1);
const totalChatter = ref(0);
const chatterLoading = ref(false);
/** ER-10596 / MDP-560 — skeleton shown by YourBadgesTable while the badges fetch is in flight. */
const loading = ref(false);

const handleGuideClose = () => {
  emit('guide-close');
};
const accountID = ref(null);
const empId = ref(null);

function applyExpirationFilterFromRoute() {
  if (isAboutToExpireRouteQuery(route.query)) {
    listQuery.value.expirationFilter = BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE;
  }
}

function applyStandingFilterFromRoute() {
  if (isObsoleteStandingRouteQuery(route.query)) {
    listQuery.value.badgeStandingFilters = ["Obsolete"];
    if (!listQuery.value.appliedFilters.includes("Current")) {
      listQuery.value.appliedFilters = [...listQuery.value.appliedFilters, "Current"];
    }
  }
}

function applyRouteFiltersFromQuery() {
  applyExpirationFilterFromRoute();
  applyStandingFilterFromRoute();
}

onMounted(() => {
  accountID.value = JSON.parse(localStorage.getItem("currentAccountID"));
  empId.value = resolveStoredAuthEmployeeId();
  applyRouteFiltersFromQuery();
  fetchEmployeeBadges();
});

watch(
  () => [route.query.expiration, route.query.standing],
  () => {
    applyRouteFiltersFromQuery();
    pagination.value = { ...pagination.value, page: 1 };
    fetchEmployeeBadges();
  }
);

function onBadgePageChange({ page }) {
  pagination.value = { ...pagination.value, page };
  fetchEmployeeBadges();
}

function onBadgesQueryChange(payload) {
  listQuery.value = {
    search: payload?.search ?? "",
    appliedFilters: Array.isArray(payload?.appliedFilters)
      ? [...payload.appliedFilters]
      : [],
    selectedLevels: Array.isArray(payload?.selectedLevels)
      ? [...payload.selectedLevels]
      : [],
    expirationFilter: payload?.expirationFilter ?? BADGE_EXPIRATION_FILTER_NONE,
    badgeStandingFilters: Array.isArray(payload?.badgeStandingFilters)
      ? payload.badgeStandingFilters.filter((f) => BADGE_STANDING_FILTER_OPTIONS.includes(f))
      : [],
  };
  pagination.value = { ...pagination.value, page: 1 };
  fetchEmployeeBadges();
}

watch(
  () => props.dataStream,
  (newVal) => {
    if (newVal.length > 0) {
      employeeBadges.value = newVal;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);
async function fetchEmployeeBadges() {
  if (accountID.value == null || empId.value == null) return;
  proxy.$loader.show();
  loading.value = true;
  try {
    const queryParams = buildEmployeeBadgesListQuery({
      page: pagination.value.page,
      perPage: pagination.value.perPage,
      listQuery: listQuery.value,
    });

    if (needsClientSideBadgeListFiltering(listQuery.value)) {
      const { limit, offset, ...baseQuery } = queryParams;
      if (!baseQuery.status) {
        baseQuery.status = "current";
      }
      const all = await getAllEmployeeBadges(accountID.value, empId.value, baseQuery);
      let filtered = all?.results || [];
      if (isAboutToExpireExpirationFilter(listQuery.value.expirationFilter)) {
        filtered = filterCurrentBadgesAboutToExpire(filtered);
      }
      if (listQuery.value.badgeStandingFilters?.length) {
        filtered = filterBadgesByStanding(filtered, listQuery.value.badgeStandingFilters);
      }
      const start = (pagination.value.page - 1) * pagination.value.perPage;
      pagination.value = { ...pagination.value, total: filtered.length };
      levelCounts.value = null;
      employeeBadges.value = mapEmployeeBadgeApiResultsToTableRows(
        filtered.slice(start, start + pagination.value.perPage)
      );
      return;
    }

    const { data } = await getEmployeeBadges(
      accountID.value,
      empId.value,
      queryParams
    );
    if (data) {
      pagination.value = {
        ...pagination.value,
        total: typeof data.count === "number" ? data.count : 0,
      };
      levelCounts.value = normalizeBadgeLevelCounts(data.level_counts);
      employeeBadges.value = mapEmployeeBadgeApiResultsToTableRows(data.results);
    }
  } catch (error) {
    console.error("Error fetching employee badges:", error);
  } finally {
    loading.value = false;
    proxy.$loader.hide();
  }
}

async function handleChatterClick(badgeData) {
  if (!badgeData || !badgeData.id) {
    console.warn("Invalid badge data for chatter:", badgeData);
    return;
  }

  // Get badge level name
  const badgeLevel = badgeData.currentBadge?.level || "";
  const badgeLevelName =
    badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1);

  // Store badge info for drawer
  selectedBadgeChatter.value = {
    id: badgeData.id,
    badgeLevelName: badgeLevelName,
    level: badgeLevel,
    responsibility: badgeData.responsibility,
  };

  // Reset chatter data when opening drawer
  chatterData.value = [];
  chatterPage.value = 1;
  totalChatter.value = 0;
  showChatterDrawer.value = true;

  // Fetch chatter data
  await fetchChatter();
}

function closeChatterDrawer() {
  showChatterDrawer.value = false;
  selectedBadgeChatter.value = null;
  chatterData.value = [];
  chatterPage.value = 1;
  totalChatter.value = 0;
}

async function fetchChatter() {
  if (
    chatterLoading.value ||
    !selectedBadgeChatter.value ||
    !selectedBadgeChatter.value.id
  )
    return;

  chatterLoading.value = true;
  try {
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const badgeId = selectedBadgeChatter.value.id;

    const { data } = await getChatterList(accountID, badgeId, "badge", chatterPage.value);

    if (data && data.count) {
      totalChatter.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name
          ? el.recorded_by_name.split(" ")
          : [""];

        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: formatBadgeChatterNote(el.note),
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        if (nameParts.length >= 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts.slice(1).join(" "),
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name || "",
            lastName: "",
          };
        }

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function loadMoreChatter() {
  if (chatterLoading.value) return;
  chatterPage.value += 1;
  await fetchChatter();
}
</script>
<style>
.your-badges-tab {
  margin-top: 15px;
}

/* Fade transition styles */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
